import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuardService } from './core/guards/auth-guard.service';
import { PeopleViewerGuard } from './core/guards/people-viewer.guard';
import { EntryPageComponent } from './pages/entry-page/entry-page.component';
import { AuthAsComponent } from './shared/components/auth-as/auth-as.component';

const routes: Routes = [
  {
    path: '',
    component: EntryPageComponent,
  },
  {
    path: 'home',
    loadChildren: () => import('./pages/home-page/home-page.module').then( m => m.HomePageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'company',
    loadChildren: () => import('./pages/company-page/company-page.module').then(m => m.CompanyPageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'company-v2',
    loadChildren: () => import('./pages/buyer/company-details-v2/company-details-v2.module').then(m => m.CompanyDetailsV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'contact',
    loadChildren: () => import('./pages/contact-page/contact-page.module').then(m => m.ContactPageModule),
    canActivate: [AuthGuardService, PeopleViewerGuard],
  },
  {
    path: 'meetings',
    loadChildren: () => import('./pages/meeting-page/meeting-page.module').then(m => m.MeetingPageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'integration',
    loadChildren: () => import('./pages/integration-page/integration-page.module').then(m => m.IntegrationPageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'insights',
    loadChildren: () => import('./pages/insights/insights.module').then(m => m.InsightsModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'profile',
    loadChildren: () => import('./pages/profile-page/profile-page.module').then(m => m.ProfilePageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'tech-editor',
    loadChildren: () => import('./pages/buyer/tech-editor-v2/tech-editor-v2.module').then(m => m.TechEditorV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'tech-editor-v2',
    loadChildren: () => import('./pages/buyer/tech-editor-v2/tech-editor-v2.module').then(m => m.TechEditorV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'tech-stack-visualizer',
    loadChildren: () => import('./pages/buyer/tech-stack-visualizer-v2/tech-stack-visualizer-v2.module').then(m => m.TechStackVisualizerV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'tech-stack-visualizer-v2',
    loadChildren: () => import('./pages/buyer/tech-stack-visualizer-v2/tech-stack-visualizer-v2.module').then(m => m.TechStackVisualizerV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'buyer-home',
    loadChildren: () => import('./pages/buyer/home-page/home-page.module').then(m => m.HomePageModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'products',
    loadChildren: () => import('./pages/buyer/product-search-v2/product-search-v2.module').then(m => m.ProductSearchV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'product-search-v2',
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/products'
      },
      {
        path: 'products',
        pathMatch: 'full',
        redirectTo: '/products'
      },
      {
        path: 'products/category/:categoryId',
        redirectTo: '/products/category/:categoryId'
      },
      {
        path: 'products/:categoryId',
        redirectTo: '/products/category/:categoryId'
      },
      {
        path: 'product/:productId',
        redirectTo: '/products/product/:productId'
      },
      {
        path: 'companies',
        redirectTo: '/products/companies'
      },
      {
        path: 'profiles',
        redirectTo: '/products/profiles'
      }
    ],
    canActivate: [AuthGuardService],
  },
  {
    path: 'product',
    children: [
      {
        path: ':productId',
        redirectTo: '/products/product/:productId'
      },
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/products'
      }
    ],
    canActivate: [AuthGuardService],
  },
  {
    path: 'setup-cohorts',
    loadChildren: () => import('./pages/buyer/setup-cohorts/setup-cohorts.module').then(m => m.SetupCohortsModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'buyer-insights',
    loadChildren: () => import('./pages/buyer/insights-v2/insights-v2.module').then(m => m.InsightsV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'buyer-insights-legacy',
    loadChildren: () => import('./pages/buyer/insights/insights.module').then(m => m.InsightsModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'buyer-insights-v2',
    loadChildren: () => import('./pages/buyer/insights-v2/insights-v2.module').then(m => m.InsightsV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'skills-intelligence',
    loadChildren: () => import('./pages/buyer/skills-intelligence/skills-intelligence.module').then(m => m.SkillsIntelligenceModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'companies',
    loadChildren: () => import('./pages/buyer/company-search-v2/company-search-v2.module').then(m => m.CompanySearchV2Module),
    canActivate: [AuthGuardService],
  },
  {
    path: 'companies-legacy',
    loadChildren: () => import('./pages/buyer/companies/companies.module').then(m => m.CompaniesModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'company-search',
    pathMatch: 'full',
    redirectTo: '/companies',
  },
  {
    path: 'company-search-legacy',
    loadChildren: () => import('./pages/buyer/company-search/company-search.module').then(m => m.CompanySearchModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'company-search-v2',
    pathMatch: 'full',
    redirectTo: '/companies',
  },
  {
    path: 'contact-search',
    loadChildren: () => import('./pages/buyer/contact-search/contact-search.module').then(m => m.ContactSearchModule),
    canActivate: [AuthGuardService, PeopleViewerGuard],
  },
  {
    path: 'ai-chat',
    loadChildren: () => import('./pages/ai/ai.module').then(m => m.AiModule),
    canActivate: [AuthGuardService],
  },
  {
    path:'transformation-journeys',
    loadChildren: () => import('./pages/buyer/plays/plays.module').then(m => m.PlaysModule),
    canActivate: [AuthGuardService],
  },
  {
    path: 'my-contacts',
    loadChildren: () => import('./pages/contacts-owned-page/contacts-owned-page.module').then(m => m.ContactsOwnedPageModule),
    canActivate: [AuthGuardService, PeopleViewerGuard],
  },
  // {
  //   path: 'companies/advanced-search',
  //    loadChildren: () => import('./pages/buyer/companies/companies.module').then(m => m.CompaniesModule)
  // },
  {
    path: 'auth-as',
    component: AuthAsComponent,
    canActivate: [AuthGuardService],
  },
  {
    path: 'validate',
    loadChildren: () => import('./pages/validate/validate.module').then(m => m.ValidateModule),
  },
  { path: '**', redirectTo: '/buyer-home'}
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
